import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import { ReactNode, useState } from "react";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import useStripeAccountId from "@/hooks/serviceFlow/useStripeAccountId";
import useErrorLogger from "@/utils/useErrorLogger";

const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_KEY;

type Props = {
  children: ReactNode;
} & StripeElementsOptions;

export const AuthenticatedStripeProvider = ({
  children,
  ...options
}: Props) => {
  const logError = useErrorLogger();

  const [stripe, setStripe] = useState(null);

  const { data, loading, error } = useStripeAccountId();

  const stripeOptions: StripeElementsOptions = options;

  useEffectOnce(Boolean(!loading && (data || error)), () => {
    if (error) {
      logError(
        `Failed to fetch Stripe Account information from medspa data: ${error.message}`
      );
      return;
    }

    if (!data?.medspaByPk?.stripeAccountId) {
      logError(
        `Stripe Setup must be completed for medspa with id: ${data?.medspaByPk?.id}.
        Moxie Suite will not work correctly until this is resolved.`
      );
      return;
    }

    loadStripe(STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: data.medspaByPk.stripeAccountId,
    })
      .then(setStripe)
      .catch((error) => {
        logError(`Failed to load Stripe: ${error}`);
      });
  });

  return (
    <Elements stripe={stripe} options={stripeOptions}>
      {children}
    </Elements>
  );
};
